// @ts-ignore - allow import with query string
import { formatMoney } from './util.js?v=25';

class ProductWizard extends HTMLElement {
  /**
   * @type {{ id: number | string; price: number | string } | null}
   * @private
   */
  _currentVariant = null;

  constructor() {
    super();

    this.elements = this.getElements();
    this.init();
  }

  init() {
    this.elements.options.forEach((el) => {
      el.addEventListener('change', (event) => this.handleOptionChange(event));

      if (el instanceof HTMLInputElement && el.checked) {
        this.handleOptionChangeForElement(el);
      }
      if (el instanceof HTMLSelectElement) {
        if (el.value) {
          this.handleOptionChangeForElement(el);
        }
      }
    });

    this.elements.form?.addEventListener('submit', (event) => this.handleAddToCart(event));
    window.addEventListener('variantUpdated', (event) => this.handleVariantUpdated(event));
  }

  /**
   * @param {Event} event
   */
  handleOptionChange(event) {
    const target = event.target;

    if (!(target instanceof HTMLElement)) {
      return;
    }

    this.handleOptionChangeForElement(target);
  }

  /**
   * @param {HTMLElement} target
   */
  handleOptionChangeForElement(target) {
    /** @type {HTMLElement | null} */
    const optionContainer = target.closest('.product-wizard__option');

    /** @type {string | null} */
    let optionValue = null;
    if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) {
      optionValue = target.value;
    }

    const optionGroupElement = target.closest('[js-product-wizard-form-group]');
    const optionGroup = optionGroupElement?.getAttribute('js-product-wizard-form-group') ?? null;

    const selectEl = optionContainer?.querySelector('select');
    if (selectEl instanceof HTMLSelectElement) {
      optionValue = selectEl.value;
    }

    if (!optionValue) {
      return;
    }

    const mainProductForm = this.mainProductForm;
    if (!(mainProductForm instanceof HTMLElement)) {
      return;
    }

    const mainProductOption = mainProductForm.querySelector(`.product-option-selector__radio[value="${optionValue}"]`);

    if (mainProductOption instanceof HTMLInputElement) {
      mainProductOption.click();
    }
  }

  enableAllSteps() {
    /**
     * @type {NodeListOf<HTMLElement>}
     */
    const steps = this.querySelectorAll('[js-product-wizard-form-group]');

    steps.forEach((step) => {
      step.removeAttribute('disabled');
    });
  }

  /**
   * @param {Event} event
   */
  async handleAddToCart(event) {
    event.preventDefault();

    const isPreOrder = this.hasAttribute('js-product-wizard-pre-order');
    const preOrderMessage = this.getAttribute('js-product-wizard-pre-order-message');

    const formData = this.elements.form instanceof HTMLFormElement ? new FormData(this.elements.form) : new FormData();

    formData.set('id', String(this.currentVariantId));
    formData.set('quantity', '1');

    if (isPreOrder) {
      formData.set('properties[Pre-order]', preOrderMessage ?? '');
    }

    this.elements.addToCart?.setAttribute('disabled', 'true');

    await fetch('/cart/add.js', { method: 'POST', body: formData });

    window.location.href = '/cart';
  }

  toggleAddToCartButton(show = false) {
    const addToCartContainer = this.elements.addToCartContainer;
    const addToCartButton = this.elements.addToCart;
    const activeClass = 'product-wizard__add-to-cart-container--is-active';

    if (show) {
      addToCartContainer?.classList.add(activeClass);
      this.classList.add('product-wizard--steps-enabled');
      addToCartButton?.removeAttribute('disabled');

      this.enableAllSteps();
    } else {
      addToCartContainer?.classList.remove(activeClass);
    }
  }

  updatePrice() {
    if (!this.currentVariant) return;

    this.elements.total.forEach((totalElement) => {
      totalElement.textContent = formatMoney(
        this.currentVariant?.price,
        `${window.theme.moneyFormat.charAt(0)}{{amount_no_decimals}}`,
      );
    });
  }

  getElements() {
    return {
      form: this.querySelector('.product-wizard__form'),
      addToCart: this.querySelector('[js-product-wizard-el="add-to-cart"]'),
      addToCartContainer: this.querySelector('[js-product-wizard-el="add-to-cart-container"]'),
      total: this.querySelectorAll('[js-product-wizard-el="total"]'),
      totalSummary: this.querySelector('[js-product-wizard-el="total-summary"]'),
      outOfStock: this.querySelectorAll('[js-product-wizard-el="out-of-stock"]'),
      options: this.querySelectorAll(
        '[js-product-wizard-form-group] input[type="radio"], [js-product-wizard-form-group] select',
      ),
    };
  }

  /**
   * @param {Event} event
   */
  handleVariantUpdated(event) {
    /** @type {CustomEvent | null} */
    const customEvent = event instanceof CustomEvent ? event : null;

    if (!customEvent || !customEvent.detail || !customEvent.detail.variant) {
      return;
    }

    this.currentVariant = customEvent.detail.variant;

    if (this.currentVariant) {
      this.updatePrice();
      this.toggleAddToCartButton(true);
    }
  }

  get mainProductForm() {
    return document.querySelector('.main-product__form');
  }

  set currentVariant(val) {
    this._currentVariant = val;
  }

  get currentVariant() {
    return this._currentVariant || null;
  }

  get currentVariantId() {
    if (this.currentVariant && this.currentVariant.id) {
      return this.currentVariant.id;
    }

    const params = new URLSearchParams(window.location.search);
    const variantFromQS = params.get('variant');

    if (variantFromQS) {
      return variantFromQS;
    }
    return null;
  }

  get hasSelectedVariant() {
    return this.currentVariant !== undefined;
  }
}

customElements.define('product-wizard', ProductWizard);
